import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/integrations/supabase/client';
import { CardImage } from '@/components/CardImage';
import { RarityBadge } from '@/components/RarityBadge';
import { InkDot } from '@/components/InkDot';
import { PriceChart } from '@/components/PriceChart';
import { usePrintHistory } from '@/hooks/usePrintHistory';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { TREATMENT_LABELS, type CardPrint } from '@/hooks/useCards';
import { cardPath, shortIdFromSlug, slugify } from '@/lib/cardSlug';
import type { Tables } from '@/integrations/supabase/types';

type Card = Tables<'cards'>;

const SITE = 'https://lorescout.com';

function formatPrice(n: number | null | undefined) {
  if (n == null) return '—';
  return `$${Number(n).toFixed(2)}`;
}

export default function CardPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const shortId = shortIdFromSlug(slug);

  const { data, isLoading } = useQuery({
    queryKey: ['card-by-slug', slug],
    enabled: !!shortId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      // Match by last 8 chars of UUID (cast to text, strip dashes).
      const { data, error } = await supabase
        .rpc('find_card_by_short_id', { short_id: shortId });
      if (!error && data && (data as any[]).length) {
        const card = (data as any[])[0] as Card;
        const { data: prints } = await supabase
          .from('card_prints')
          .select('*')
          .eq('card_id', card.id)
          .order('treatment');
        return { card, prints: (prints || []) as CardPrint[] };
      }
      // Fallback: scan by name slug if RPC missing.
      const nameGuess = slug.replace(/-[a-f0-9]{8}$/i, '').split('-').slice(0, 4).join(' ');
      const { data: list } = await supabase
        .from('cards')
        .select('*')
        .ilike('name', `%${nameGuess}%`)
        .limit(50);
      const match = (list || []).find(c =>
        c.id.replace(/-/g, '').toLowerCase().endsWith((shortId || '').toLowerCase())
      ) as Card | undefined;
      if (!match) return null;
      const { data: prints } = await supabase
        .from('card_prints')
        .select('*')
        .eq('card_id', match.id)
        .order('treatment');
      return { card: match, prints: (prints || []) as CardPrint[] };
    },
  });

  if (isLoading) {
    return (
      <div className="app-container flex min-h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!data?.card) {
    return (
      <div className="app-container min-h-screen p-6 text-center">
        <Helmet>
          <title>Card not found · LoreScout</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <h1 className="text-2xl font-serif mt-12">Card not found</h1>
        <p className="mt-2 text-muted-foreground">
          We couldn't find that card. <Link to="/" className="underline">Back to LoreScout</Link>.
        </p>
      </div>
    );
  }

  const { card, prints } = data;
  const fullName = card.subtitle ? `${card.name} — ${card.subtitle}` : card.name;
  const canonical = `${SITE}${cardPath(card)}`;

  const normalPrint = prints.find(p => p.treatment === 'normal');
  const priceLines = prints
    .filter(p => p.price_usd != null)
    .map(p => `${TREATMENT_LABELS[p.treatment]?.label || p.treatment}: ${formatPrice(p.price_usd)}`);

  const description = [
    `${fullName} (${card.set_name}) — Disney Lorcana ${card.rarity} ${card.card_type}.`,
    card.cost != null ? `Cost ${card.cost}.` : null,
    priceLines.length ? `Live prices: ${priceLines.slice(0, 4).join(', ')}.` : null,
    'Track prices, history and your collection on LoreScout.',
  ].filter(Boolean).join(' ').slice(0, 320);

  const jsonLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: fullName,
    description: `${fullName} — Disney Lorcana ${card.rarity} ${card.card_type} from ${card.set_name}.`,
    image: card.image_url || undefined,
    category: 'Trading Card Game',
    brand: { '@type': 'Brand', name: 'Disney Lorcana' },
    sku: card.lorcast_id || card.id,
    url: canonical,
  };
  if (normalPrint?.price_usd != null) {
    jsonLd.offers = {
      '@type': 'Offer',
      price: Number(normalPrint.price_usd).toFixed(2),
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      url: canonical,
    };
  }

  return (
    <div className="app-container min-h-screen pb-12">
      <Helmet>
        <title>{`${fullName} · ${card.set_name} · Lorcana Price · LoreScout`}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={`${fullName} · Lorcana Price`} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        {card.image_url && <meta property="og:image" content={card.image_url} />}
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <header className="flex items-center gap-3 p-4 border-b border-border">
        <Link to="/" className="text-sm underline">← LoreScout</Link>
      </header>

      <article className="px-4 pt-4">
        <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground mb-3">
          <Link to="/" className="hover:underline">Home</Link>
          {' / '}
          <span>Cards</span>
          {' / '}
          <span className="text-foreground">{fullName}</span>
        </nav>

        <h1 className="font-serif text-2xl leading-tight">
          {card.name}
          {card.subtitle && <span className="block text-base text-muted-foreground italic mt-1">{card.subtitle}</span>}
        </h1>

        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <InkDot ink={card.ink_color as any} />
          <RarityBadge rarity={card.rarity as any} />
          <span className="text-xs text-muted-foreground">{card.set_name}</span>
          {card.card_type && <span className="text-xs text-muted-foreground">· {card.card_type}</span>}
        </div>

        <div className="mt-5 grid gap-5 sm:grid-cols-[minmax(0,260px)_1fr] items-start">
          <CardImage
            src={card.image_url}
            alt={`${fullName} — Disney Lorcana ${card.rarity} card from ${card.set_name}`}
            inkColor={card.ink_color as any}
            enchanted={card.enchanted ?? false}
          />

          <div className="space-y-4 text-sm">
            <dl className="grid grid-cols-2 gap-2">
              {card.cost != null && (<><dt className="text-muted-foreground">Cost</dt><dd>{card.cost}</dd></>)}
              {card.strength != null && (<><dt className="text-muted-foreground">Strength</dt><dd>{card.strength}</dd></>)}
              {card.willpower != null && (<><dt className="text-muted-foreground">Willpower</dt><dd>{card.willpower}</dd></>)}
              {card.lore_value != null && (<><dt className="text-muted-foreground">Lore</dt><dd>{card.lore_value}</dd></>)}
              {card.inkable != null && (<><dt className="text-muted-foreground">Inkable</dt><dd>{card.inkable ? 'Yes' : 'No'}</dd></>)}
            </dl>

            {card.abilities && (
              <section>
                <h2 className="font-semibold text-foreground mb-1">Abilities</h2>
                <p className="whitespace-pre-line text-muted-foreground">{card.abilities}</p>
              </section>
            )}

            {card.flavor_text && (
              <p className="italic text-muted-foreground border-l-2 border-border pl-3">{card.flavor_text}</p>
            )}
          </div>
        </div>

        <section className="mt-8">
          <h2 className="font-serif text-xl mb-3">Prices by rarity / treatment</h2>
          <div className="grid gap-2">
            {prints.length === 0 && <p className="text-sm text-muted-foreground">No price data yet for this card.</p>}
            {prints.map(p => {
              const meta = TREATMENT_LABELS[p.treatment] || { label: p.treatment, emoji: '🃏' };
              return (
                <div key={p.id} className="flex items-center justify-between rounded-md border border-border px-3 py-2">
                  <span className="text-sm">
                    <span aria-hidden className="mr-1">{meta.emoji}</span>
                    {meta.label}
                  </span>
                  <span className="text-sm font-medium tabular-nums">{formatPrice(p.price_usd)}</span>
                </div>
              );
            })}
          </div>
        </section>

        {normalPrint && <PriceHistorySection printId={normalPrint.id} label={`${card.name} (Normal)`} />}


        <section className="mt-10 rounded-lg border border-border p-4 text-sm">
          <h2 className="font-serif text-lg mb-1">Track {card.name} in your collection</h2>
          <p className="text-muted-foreground">
            LoreScout tracks live market prices, full price history, and every rarity (Cold Foil,
            Enchanted, Epic, Iconic, Rainbow Foil and more) for every Disney Lorcana card.
          </p>
          <Link to="/" className="inline-block mt-3 underline">Open LoreScout →</Link>
        </section>
      </article>
    </div>
  );
}

function PriceHistorySection({ printId, label }: { printId: string; label: string }) {
  const { data } = usePrintHistory([printId]);
  const points = data?.get(printId) || [];
  if (points.length < 2) return null;
  return (
    <section className="mt-8">
      <h2 className="font-serif text-xl mb-3">Price history</h2>
      <PriceChart series={[{ id: printId, label, color: 'hsl(var(--primary))', data: points }]} />
    </section>
  );
}

